/**
 * widgetPresets — what a design-system widget tile drops: its content type,
 * cell size, constraints and starting props.
 *
 * Presets are FUNCTIONS so every drag gets fresh objects (the grid stores the
 * spec as given). They carry no form field name: a name is allocated when the
 * drop commits, against the destination section's inputs (fieldNames.ts).
 * Sizes and props follow the matching section templates (sectionTemplates.ts).
 */
import type { GridItemConstraints } from "@fredericrous/grid";
// The creation gate lives with the schemas, so the authoring tools (MCP)
// read the same switch (creationGates.ts).
import { ICON_INLINE_CREATION } from "@website-builder/schemas";

export interface WidgetPreset {
  readonly contentType: string;
  readonly w: number;
  readonly h: number;
  readonly constraints: GridItemConstraints;
  readonly contentProps: Record<string, unknown>;
}

export interface DsWidgetTile {
  /** Palette id — `w-` prefixed so it never collides with a section template id. */
  readonly id: string;
  readonly icon: string;
  readonly label: string;
  readonly preset: () => WidgetPreset;
}

export { ICON_INLINE_CREATION };

/** The Icon and Inline tiles, offered only once creation is on. The Inline is
 *  a one-row container: its drop creates its (empty) nested grid, see
 *  prepareInlineExternalAdd. */
export const ICON_INLINE_TILES: readonly DsWidgetTile[] = [
  {
    id: "w-inline",
    icon: "↔",
    label: "Inline",
    preset: () => ({
      contentType: "inline-grid",
      w: 6,
      h: 1,
      constraints: { minH: 1, maxH: 1, minW: 1 },
      contentProps: { justify: "start" },
    }),
  },
  {
    id: "w-icon",
    icon: "⭐",
    label: "Icon",
    preset: () => ({
      contentType: "icon",
      w: 1,
      h: 1,
      constraints: { minH: 1, minW: 1 },
      contentProps: { name: "search" },
    }),
  },
];

/** The design-system tiles the Add menu shows, gate applied. */
export function dsWidgetTiles(
  iconInline: boolean = ICON_INLINE_CREATION,
): readonly DsWidgetTile[] {
  return iconInline
    ? [...DS_WIDGET_TILES, ...ICON_INLINE_TILES]
    : DS_WIDGET_TILES;
}

export const DS_WIDGET_TILES: readonly DsWidgetTile[] = [
  {
    id: "w-button",
    icon: "🔘",
    label: "Button",
    preset: () => ({
      contentType: "button",
      w: 3,
      h: 1,
      constraints: { minH: 1, minW: 2 },
      contentProps: {
        label: "Get started",
        variant: "primary",
        size: "default",
        // The cell is the button's width, and a taller cell is space around
        // it: resizing the cell resizes the button, the label keeps its size.
        width: "fill",
        valign: "middle",
      },
    }),
  },
  {
    id: "w-link-button",
    icon: "🔗",
    label: "Link button",
    preset: () => ({
      contentType: "button",
      w: 3,
      h: 1,
      constraints: { minH: 1, minW: 2 },
      contentProps: {
        label: "Learn more",
        href: "/",
        variant: "secondary",
        width: "fill",
        valign: "middle",
      },
    }),
  },
  {
    id: "w-heading",
    icon: "🔠",
    label: "Heading",
    preset: () => ({
      contentType: "heading",
      w: 12,
      h: 2,
      constraints: { minH: 1 },
      contentProps: { text: "Section heading", level: 2 },
    }),
  },
  {
    id: "w-card",
    icon: "🗂️",
    label: "Card",
    preset: () => ({
      contentType: "card",
      w: 5,
      h: 4,
      constraints: { minH: 2, minW: 3 },
      contentProps: {
        title: "Card title",
        body: "A short description goes here.",
        variant: "outlined",
      },
    }),
  },
  {
    id: "w-input",
    icon: "⌨️",
    label: "Input",
    preset: () => ({
      contentType: "input",
      w: 6,
      h: 1,
      constraints: { minH: 1, minW: 3 },
      contentProps: { label: "Field", type: "text" },
    }),
  },
  {
    id: "w-input-group",
    icon: "🔍",
    label: "Input group",
    preset: () => ({
      contentType: "input",
      w: 6,
      h: 1,
      constraints: { minH: 1, minW: 3 },
      contentProps: {
        label: "Search",
        type: "search",
        icon: "search",
        placeholder: "Search",
      },
    }),
  },
];

/** Kinds whose settings open right after a drop: their preset is a
 *  placeholder nobody keeps. */
export const OPEN_SETTINGS_ON_DROP: ReadonlySet<string> = new Set(["input"]);
